import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { routes } from '../../routes/Routes';

import { MenuPage } from '../../layouts';
import { PageTitle, Table, Text } from '../../components';

import { selectors as accessUrlSelectors, actions as accessUrlActions } from '../../redux/accessUrls';
import { selectors as workflowsSelectors, actions as workflowsActions } from '../../redux/workflows';

import { makeGet } from '../../api/ajax';

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const formatExpiresAt = (expiresAt) => {
  if (!expiresAt) return 'Never expires (valid)';

  // NOTE: expiresAt is returned by the API without a timezone designator, but is always in UTC (0 offset)
  const expiresAtUtc = new Date(expiresAt);
  const status = expiresAtUtc > new Date() ? 'valid' : 'expired';

  return `${expiresAt} (${status})`;
};

const buildFieldRows = (accessUrl, workflowName) => [
  { fieldName: 'VIN', value: formatFieldValue(accessUrl.vin) },
  { fieldName: 'Registration No', value: formatFieldValue(accessUrl.registrationNo) },
  { fieldName: 'External Id', value: formatFieldValue(accessUrl.externalId) },
  { fieldName: 'Expires At', value: formatExpiresAt(accessUrl.expiresAt) },
  { fieldName: 'Report Copy Email', value: formatFieldValue(accessUrl.reportCopyEmail) },
  { fieldName: 'One Time Use', value: formatFieldValue(accessUrl.oneTimeUse) },
  { fieldName: 'Workflow', value: formatFieldValue(workflowName) },
  { fieldName: 'Url', value: formatFieldValue(accessUrl.url) },
];

const AccessUrls = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useDispatch();
  const accessUrl = useSelector((state) => accessUrlSelectors.selectAccessUrl(state, id));
  const hasWorkflows = useSelector(workflowsSelectors.hasWorkflows);
  const workflow = useSelector((state) => {
    const workflows = workflowsSelectors.selectWorkFlows(state);
    return accessUrl?.workflowId && workflows ? workflows[accessUrl.workflowId] : undefined;
  });

  useEffect(() => {
    if (accessUrl) {
      setIsLoading(false);
      return;
    }

    makeGet(`accessUrls/${id}`).subscribe(({ response }) => {
      dispatch(accessUrlActions.loadAccessUrlSuccess(id, response));
      setIsLoading(false);
    });
  }, [id, accessUrl, dispatch]);

  useEffect(() => {
    if (hasWorkflows) return;

    makeGet('workflows/currentCustomer/admin?showDeleted=true').subscribe(({ response }) => {
      dispatch(workflowsActions.loadWorkflowsSuccess(response));
    });
  }, [hasWorkflows, dispatch]);

  return (
    <MenuPage currentRoute={routes.vehicles}>
      <PageTitle>
        <PageTitle.Left headingText="Capture link details" isLoading={isLoading} />
      </PageTitle>
      {
        accessUrl && (
          <div className="row">
            <div className="col-sm-12">
              <Table
                rows={buildFieldRows(accessUrl, workflow?.name).map(({ fieldName, value }) => ({
                  id: fieldName,
                  name: fieldName,
                  row: (
                    <>
                      <div className="col-sm-3 row-text">
                        <Text size={2} weight="bold" inline>{fieldName}</Text>
                      </div>
                      <div className="col-sm-9 row-text">
                        <Text size={2} inline>{value}</Text>
                      </div>
                    </>
                  ),
                }))}
              />
            </div>
          </div>
        )
      }
    </MenuPage>
  );
};

export default AccessUrls;
